import React from 'react';
import { Loader2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

interface OptimizedLoadingScreenProps {
  isLoading: boolean;
  error: string | null;
  loadingProgress: number;
  currentPhase: string;
  onRetry?: () => void;
}

export const OptimizedLoadingScreen: React.FC<OptimizedLoadingScreenProps> = ({
  isLoading,
  error,
  loadingProgress,
  currentPhase,
  onRetry
}) => {
  if (!isLoading && !error) {
    return null;
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error de Carga
          </h2>
          <p className="text-gray-600 mb-6">
            {error}
          </p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reintentar
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 text-center">
        {/* Spinner animado */}
        <div className="relative mb-6">
          <div className="w-20 h-20 mx-auto">
            <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
            <div 
              className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"
              style={{
                background: `conic-gradient(from 0deg, #2563eb ${loadingProgress * 3.6}deg, #e5e7eb 0deg)`
              }}
            ></div>
            <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-gray-700">
                {Math.round(loadingProgress)}%
              </span>
            </div>
          </div>
        </div>

        {/* Fase actual */}
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Cargando Aplicación
        </h2>
        <p className="text-gray-600 mb-4">
          {currentPhase}
        </p>

        {/* Barra de progreso */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${loadingProgress}%` }}
          ></div>
        </div>

        {/* Indicadores de estado */}
        <div className="flex justify-center space-x-2">
          {loadingProgress >= 25 && (
            <CheckCircle className="h-5 w-5 text-green-500" />
          )}
          {loadingProgress >= 50 && (
            <CheckCircle className="h-5 w-5 text-green-500" />
          )}
          {loadingProgress >= 75 && (
            <CheckCircle className="h-5 w-5 text-green-500" />
          )}
          {loadingProgress >= 100 && (
            <CheckCircle className="h-5 w-5 text-green-500" />
          )}
        </div>

        {/* Mensaje de optimización */}
        <p className="text-xs text-gray-500 mt-4">
          Optimizando conexión a la base de datos...
        </p>
      </div>
    </div>
  );
};

// Componente de carga simple para componentes individuales
export const SimpleLoadingSpinner: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ 
  size = 'md' 
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8'
  };

  return (
    <div className="flex items-center justify-center p-4">
      <Loader2 className={`${sizeClasses[size]} animate-spin text-blue-600`} />
    </div>
  );
};

// Componente de carga con texto
export const LoadingWithText: React.FC<{ text: string; size?: 'sm' | 'md' | 'lg' }> = ({ 
  text, 
  size = 'md' 
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8'
  };

  return (
    <div className="flex items-center space-x-2">
      <Loader2 className={`${sizeClasses[size]} animate-spin text-blue-600`} />
      <span className="text-gray-600">{text}</span>
    </div>
  );
};
